import axios from 'axios'
import {API_URL} from '../../../../../../_metronic/helpers'
import {SearchSelectOption} from '../../../../../../_metronic/helpers/components/SearchSelect'
import {formatInvoiceCount} from './_formatters'

// «نوع خطا» of the invoice list (backend api/invoices/error-reasons): the errors the selected business's failed
// invoices carry in the list's season, grouped as the reports' «علت خطاها» table groups them. A reason travels as
// a short key — code:0300101 for the organisation's code, text: and 16 hex digits for a refusal's text — so a
// Persian text never has to fit in the query string.

/** One error the failed invoices of the scope carry. */
export type InvoiceErrorReason = {
  key: string
  /** The organisation's code; empty for a refusal written before the send. */
  code: string
  message: string
  invoices: number
  amount: number
}

export type InvoiceErrorReasonList = {
  season?: string
  total: number
  truncated: boolean
  readClock?: string
  reasons: InvoiceErrorReason[]
}

export type InvoiceErrorReasonScope = {
  baseMode: number
  /** The list's season as it is sent: 'all' when a day range is filtered. */
  invoiceSeason?: string
  fromDate?: string
  toDate?: string
  saveFromDate?: string
  saveToDate?: string
}

export const MAX_ERROR_REASONS = 10
export const MAX_ERROR_SEARCH_LENGTH = 100
const MAX_OPTION_MESSAGE_LENGTH = 140
const MAX_TITLE_LENGTH = 60
const LABEL_STORAGE_KEY = 'MegaPay.invoiceErrorReasonLabels'
const MAX_STORED_LABELS = 200

const ERROR_REASON_KEY = /^(code:[0-9A-Za-z][0-9A-Za-z._-]{0,31}|text:[0-9a-f]{16})$/

/** A key as the backend accepts it (code:… or text: and a lower-case hash), or '' for anything else. */
export const normalizeErrorReasonKey = (value?: string | null): string => {
  const raw = (value || '').trim()
  const separator = raw.indexOf(':')
  if (separator < 0) return ''
  const prefix = raw.slice(0, separator).toLowerCase()
  const rest = raw.slice(separator + 1)
  const key = `${prefix}:${prefix === 'text' ? rest.toLowerCase() : rest}`
  return ERROR_REASON_KEY.test(key) ? key : ''
}

/** The distinct valid keys of a list, at most as many as the list accepts. */
export const normalizeErrorReasonKeys = (values?: Array<string | null | undefined>): string[] =>
  Array.from(new Set((values || []).map(normalizeErrorReasonKey).filter(Boolean))).slice(
    0,
    MAX_ERROR_REASONS
  )

/** The searched text on one line, cut to what the backend reads. */
export const normalizeErrorSearch = (value?: string | null): string =>
  (value || '').replace(/\s+/g, ' ').trim().slice(0, MAX_ERROR_SEARCH_LENGTH)

const cut = (text: string, length: number) =>
  text.length > length ? `${text.slice(0, length - 1).trimEnd()}…` : text

/** The reason as one line: «0300101 — مقدار فیلد …». */
export const errorReasonText = (reason: Pick<InvoiceErrorReason, 'code' | 'message'>): string => {
  const message = (reason.message || '').trim() || 'خطای بدون شرح'
  return reason.code ? `${reason.code} — ${message}` : message
}

// The names of the keys this browser has seen, so a filter chip opened from the reports or kept from an earlier
// visit names its reason instead of a hash. Kept for the tab only; a key it does not know reads as a code or as
// «نوع خطای انتخاب‌شده».
const labels = new Map<string, string>()

const readStoredLabels = () => {
  try {
    const stored = window.sessionStorage.getItem(LABEL_STORAGE_KEY)
    const parsed = stored ? (JSON.parse(stored) as Record<string, string>) : {}
    Object.entries(parsed).forEach(([key, label]) => {
      if (normalizeErrorReasonKey(key) && typeof label === 'string') labels.set(key, label)
    })
  } catch {
    // Storage may be blocked; the names are a convenience.
  }
}

if (typeof window !== 'undefined') readStoredLabels()

/** Remembers the names of reasons just read (the list's options, the reports' rows). */
export const rememberErrorReasons = (
  reasons?: Array<Partial<Pick<InvoiceErrorReason, 'key' | 'code' | 'message'>>>
) => {
  ;(reasons || []).forEach((reason) => {
    const key = normalizeErrorReasonKey(reason.key)
    if (key)
      labels.set(key, errorReasonText({code: reason.code || '', message: reason.message || ''}))
  })
  try {
    const kept = Array.from(labels.entries()).slice(-MAX_STORED_LABELS)
    window.sessionStorage.setItem(LABEL_STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)))
  } catch {
    // Storage may be blocked or full; the names stay for this page.
  }
}

/** A key's name for a chip or a selected option, cut to fit. */
export const errorReasonTitle = (key: string, length = MAX_TITLE_LENGTH): string => {
  const normalized = normalizeErrorReasonKey(key)
  const known = labels.get(normalized)
  if (known) return cut(known, length)
  if (normalized.startsWith('code:')) return `کد ${normalized.slice('code:'.length)}`
  return 'نوع خطای انتخاب‌شده'
}

/** An option of the «نوع خطا» select: the code in Latin digits, the message, how many invoices hold it. */
export const buildErrorReasonOption = (reason: InvoiceErrorReason): SearchSelectOption => {
  const message = cut((reason.message || '').trim() || 'خطای بدون شرح', MAX_OPTION_MESSAGE_LENGTH)
  const count = formatInvoiceCount(reason.invoices)
  return {
    value: reason.key,
    searchText: `${reason.code} ${reason.message} ${count}`,
    label: (
      <span className='mp-error-reason-option' title={errorReasonText(reason)}>
        {reason.code ? (
          <bdi className='mp-error-reason-option__code englishText' dir='ltr'>
            {reason.code}
          </bdi>
        ) : (
          <span className='mp-error-reason-option__code mp-error-reason-option__code--local'>
            پیش از ارسال
          </span>
        )}
        <span className='mp-error-reason-option__message'>{message}</span>
        <span className='mp-error-reason-option__count'>({count})</span>
      </span>
    ),
  }
}

/** A chosen key the options do not hold (a link, a reason past the first fifty): named as well as it can be. */
export const buildSelectedErrorReasonOption = (key: string): SearchSelectOption => ({
  value: key,
  searchText: errorReasonTitle(key, 200),
  label: errorReasonTitle(key),
})

export const getInvoiceErrorReasonsQueryKey = (companyId: number, scope: InvoiceErrorReasonScope) =>
  [
    'invoice-error-reasons',
    companyId,
    scope.baseMode,
    scope.invoiceSeason || 'latest',
    scope.fromDate || '',
    scope.toDate || '',
    scope.saveFromDate || '',
    scope.saveToDate || '',
  ] as const

export const getInvoiceErrorReasons = (
  scope: InvoiceErrorReasonScope,
  refresh = false
): Promise<InvoiceErrorReasonList> =>
  axios
    .get<InvoiceErrorReasonList>(`${API_URL}/invoices/error-reasons`, {
      params: {
        basemode: scope.baseMode,
        invoiceSeason: scope.invoiceSeason || undefined,
        filter_fromdate: scope.fromDate || undefined,
        filter_todate: scope.toDate || undefined,
        filter_saveFromdate: scope.saveFromDate || undefined,
        filter_saveTodate: scope.saveToDate || undefined,
        refresh: refresh || undefined,
      },
    })
    .then((response) => {
      const data = response.data || ({} as InvoiceErrorReasonList)
      const reasons = Array.isArray(data.reasons)
        ? data.reasons.filter((reason) => normalizeErrorReasonKey(reason?.key))
        : []
      rememberErrorReasons(reasons)
      return {...data, total: data.total || 0, truncated: Boolean(data.truncated), reasons}
    })
